import { createSlice, isPending, isRejected } from '@reduxjs/toolkit';
import { RootState } from '../..';
import { ICourse } from '../CoursesSlice';
import { createTrial, createTrialWithAccount, getTrials } from './api';
import { ISection } from '../SectionSlice';
import {
  createTrialAsAdmin,
  getTrialsForAdmin,
  loadMoreTrialsForAdmin,
} from './adminApi';
import { IUser } from '../../UserSlice';
import {
  setAuthTokenToLocalStorage,
  setAuthUserToLocalStorage,
} from '../../../helper';

export type TTrialStatus =
  | 'COMPLETED'
  | 'SCHEDULED'
  | 'PENDING'
  | 'CANCELED'
  | 'SUCCEED';

export const TRIAL_STATUS = {
  COMPLETED: 'COMPLETED',
  SCHEDULED: 'SCHEDULED',
  PENDING: 'PENDING',
  CANCELED: 'CANCELED',
  SUCCEED: 'SUCCEED',
};

export interface ITrialClass {
  _id?: string;
  course: ICourse;
  section: string;
  student: IUser;
  tutor: IUser;
  status: TTrialStatus;
  dateTime: string;
  duration: number;
  bookedBy: IUser;
}

export interface IExtendedSection extends Omit<ISection, 'course'> {
  course: ICourse; // Redefining the course property
}

export interface ITrialClasses {
  upcomingTrials: Array<ITrialClass>;
  completedTrials: Array<ITrialClass>;
}

interface ITrialsState {
  status: 'idle' | 'loading' | 'rejected';
  bookTrialModalOpen: boolean;
  courseSubscriptionSession?: ICourse;
  trials: ITrialClasses;
  // Admins
  adminTrials?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<ITrialClass>;
  };
}

const initialState: ITrialsState = {
  status: 'idle',
  bookTrialModalOpen: false,
  courseSubscriptionSession: undefined,
  trials: {
    upcomingTrials: [],
    completedTrials: [],
  },
  // Admin
  adminTrials: undefined,
};

export const TrialsSlice = createSlice({
  name: 'trials',
  initialState,
  reducers: {
    toggleBookTrialModal: (state) => {
      state.bookTrialModalOpen = !state.bookTrialModalOpen;
    },
    resetAdminTrials: (state) => {
      state.adminTrials = undefined;
    },
    resetTrials: (state) => {
      state.trials = initialState.trials;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createTrial.fulfilled, (state, action) => {
        state.status = 'loading';
        state.courseSubscriptionSession = action.payload;
        if (action.payload && action.payload._id) {
          window.location.href = `${process.env.PUBLIC_URL}/portal/courses/${action.payload._id}`;
        }
      })
      .addCase(createTrialWithAccount.fulfilled, (state, action) => {
        state.status = 'idle';
        setAuthTokenToLocalStorage(action.payload.access_token);
        setAuthUserToLocalStorage(action.payload.oauthUser);
        const newUrl = `${process.env.PUBLIC_URL}?trialClassCreated`;
        window.location.href = newUrl;
      })
      .addCase(getTrials.fulfilled, (state, action) => {
        state.status = 'idle';
        state.trials = action.payload;
      })
      // Admin book trials
      .addCase(createTrialAsAdmin.fulfilled, (state, action) => {
        state.status = 'loading';
        state.courseSubscriptionSession = action.payload;
      })
      // Admin get trials
      .addCase(getTrialsForAdmin.fulfilled, (state, action) => {
        state.status = 'idle';
        const res = action.payload;
        const newAdminTrials = {
          limit: res.limit,
          offset: res.offset,
          totalDocs: res.totalDocs || 0,
          list: res.docs || [],
        };
        state.adminTrials = newAdminTrials;
      })
      // Admin load more trial
      .addCase(loadMoreTrialsForAdmin.fulfilled, (state, action) => {
        state.status = 'idle';
        if (
          state.adminTrials &&
          state.adminTrials.list &&
          state.adminTrials.list.length &&
          action.payload &&
          action.payload.docs &&
          action.payload.docs.length
        ) {
          const newAdminTrials = {
            ...state.adminTrials,
            list: [...state.adminTrials.list, ...action.payload.docs],
          };
          state.adminTrials = newAdminTrials;
        }
      });

    // Matchers
    builder
      .addMatcher(
        isPending(
          createTrial,
          getTrials,
          createTrialAsAdmin,
          getTrialsForAdmin,
          loadMoreTrialsForAdmin,
          createTrialWithAccount
        ),
        (state) => {
          state.status = 'loading';
        }
      )
      .addMatcher(
        isRejected(
          createTrial,
          getTrials,
          createTrialAsAdmin,
          getTrialsForAdmin,
          loadMoreTrialsForAdmin,
          createTrialWithAccount
        ),
        (state) => {
          state.status = 'rejected';
        }
      );
  },
});

export const {
  toggleBookTrialModal,
  resetAdminTrials,
  resetTrials,
} = TrialsSlice.actions;

export const getTrialsState = (state: RootState): RootState['trials'] =>
  state.trials;

export * from './api';
export default TrialsSlice.reducer;
